/* eslint-disable react/jsx-no-useless-fragment */
import React, { memo, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Flex, Loader, Card } from './primitives';
import { getSavedSearches, deleteSavedSearch } from '../BackendAPI';
import { getUser } from '../utils/Auth2';

export const SavedSearchesLoadingFallback = () => {
  return (
    <Flex justify="center" fill marginTop="100" marginBottom="100">
      <Loader />
    </Flex>
  );
};

const TableContainer = styled.div`
  width: 100%;
  border-radius: ${({ borderTop }) => (borderTop ? '4px' : '0')};
  border-top: ${({ borderTop }) => (borderTop ? '1px solid #ebeaea' : 'none')};
  border-bottom: 1px solid #ebeaea;
  border-left: 1px solid #ebeaea;
  border-right: 1px solid #ebeaea;
  overflow-y: auto;
  height: 200px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 80% 10% 10%;
  background-color: #f8f9fa;
  border-bottom: 1px solid #ebeaea;
  font-weight: 600;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 80% 10% 10%;
  padding: 12px 16px;
  align-items: center;
  background-color: ${({ isEven }) => (isEven ? '#f8f9fa' : '#ffffff')};
  border-bottom: 1px solid #ebeaea;

  &:last-child {
    border-bottom: none;
  }
`;

const SearchLabel = styled.a`
  color: #2579a9;
  text-decoration: none;
  font-weight: 500;
  word-break: break-word;

  &:hover {
    text-decoration: underline;
    color: #1a5a7a;
  }
`;

const CreatedBy = styled.span`
  color: #666;
  font-size: 12px;
  word-break: normal;
  white-space: nowrap;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;

  &:hover {
    background-color: #f8d7da;
  }
`;

const SavedSearches = memo(({ showDeleteButton = true, borderTop = true }) => {
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const user = getUser();

  const fetchSavedSearches = useCallback(async () => {
    try {
      setLoading(true);
      const searches = await getSavedSearches();
      setSavedSearches(searches);
      setError(null);
    } catch (err) {
      setError('Failed to load saved searches');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteSearch = useCallback(
    async (id) => {
      setDeleteId(id);
      try {
        await deleteSavedSearch(id);
        await fetchSavedSearches();
      } catch (err) {
        setError('Failed to delete saved search');
      } finally {
        setDeleteId(null);
      }
    },
    [fetchSavedSearches],
  );

  useEffect(() => {
    fetchSavedSearches();
  }, [fetchSavedSearches]);

  if (loading) {
    return <SavedSearchesLoadingFallback />;
  }

  if (error) {
    return (
      <Flex justify="center" fill>
        <div style={{ color: '#dc3545', textAlign: 'center' }}>{error}</div>
      </Flex>
    );
  }

  if (savedSearches.length === 0) {
    return (
      <Flex justify="center" fill marginTop="100" marginBottom="100">
        <div style={{ textAlign: 'center', color: '#666' }}>No saved searches found</div>
      </Flex>
    );
  }

  return (
    <TableContainer borderTop={borderTop}>
      <TableHeader>
        <div>Label</div>
        <div>Owner</div>
        <div />
      </TableHeader>
      {savedSearches.map((search, index) => (
        <TableRow key={search.id} isEven={index % 2 === 1}>
          <div>
            <SearchLabel href={search.url} target="_blank" rel="noopener noreferrer">
              {search.label}
            </SearchLabel>
          </div>
          <CreatedBy>{search.user_name}</CreatedBy>
          {showDeleteButton && user?.user_id === search.user_id && (
            <>
              {deleteId === search.id ? (
                <Loader />
              ) : (
                <DeleteButton
                  onClick={() => handleDeleteSearch(search.id)}
                  title="Delete saved search"
                >
                  <i className="fa fa-trash with-margin" />
                </DeleteButton>
              )}
            </>
          )}
        </TableRow>
      ))}
    </TableContainer>
  );
});

SavedSearches.displayName = 'SavedSearches';

export default SavedSearches;
